import { bitcoinjs } from '@optimex-xyz/btc-script'
import { broadcastTx } from '@optimex-xyz/trade-wallet-sdk'

import { INetwork } from '@/types'
import { isBtcChain, isMobileDevice, SUPPORTED_NETWORK, WALLET_BINANCE } from '@/utils'
import { CustomError } from '@/utils/error'
import { generatePsbtSendBtc } from '@/utils/transaction'
import { BinanceProvider } from '@/wallets/types/binance.type'

import { BaseWallet } from './BaseWallet'
import { IWalletConnectOptions, PsbtSignOptions, TransactionOptions } from './types'

const MAINNET = 'livenet'

function getProvider(): BinanceProvider | undefined {
  return window.unisat?.isBinance ? window.binancew3w?.bitcoin : undefined
}

export class BinanceWallet extends BaseWallet {
  name = 'Binance Wallet'
  key = WALLET_BINANCE
  private provider: BinanceProvider | null = null
  private address: string = ''
  private publicKey: string = ''

  private download(): void {
    throw new CustomError(
      'Binance Wallet is not installed! <a target="_blank" class="underline" href="https://www.binance.com/en/download?pageType=normal">Download wallet</a>'
    )
  }

  private getWalletProvider(): BinanceProvider | null {
    const provider = getProvider()
    if (provider) {
      return provider
    }
    this.download()
    return null
  }

  initialize() {
    if (!this.provider) {
      this.provider = this.getWalletProvider()
    }
  }

  isInstalled(): boolean {
    return !!getProvider()
  }

  async connect(
    networkId: string,
    options: IWalletConnectOptions
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    this.initialize()
    if (!this.provider) {
      throw new CustomError('Binance wallet is not installed')
    }

    if (networkId !== SUPPORTED_NETWORK.BTC) {
      throw new Error(`Unsupported network ${networkId}`)
    }

    const currentNetwork =
      (await this.provider.getNetwork()) === MAINNET ? SUPPORTED_NETWORK.BTC : SUPPORTED_NETWORK.BTC_TESTNET

    if (currentNetwork !== SUPPORTED_NETWORK.BTC && networkId === SUPPORTED_NETWORK.BTC) {
      await this.provider.switchNetwork(MAINNET)
    }

    const accounts = await this.provider.requestAccounts()
    const publicKey = await this.provider.getPublicKey()

    this.address = accounts[0]
    this.publicKey = publicKey

    return {
      address: accounts[0],
      networkInfo: options.network,
      compressedPublicKey: publicKey,
    }
  }

  async disconnect(): Promise<void> {
    this.provider = null
  }

  async sendTransaction(
    toAddress: string,
    amount: number | bigint,
    networkId: string,
    options?: TransactionOptions
  ): Promise<string> {
    if (!this.provider) {
      throw new CustomError('Binance wallet is not installed')
    }
    if (networkId !== SUPPORTED_NETWORK.BTC) {
      throw new Error(`Unsupported network ${networkId}`)
    }
    const fromAddress = this.address

    const { psbt, inputsToSign } = await generatePsbtSendBtc({
      toAddress,
      _amount: amount,
      fromAddress,
      networkId,
      publicKey: this.publicKey,
      options,
    })

    // Sign the transaction
    const signedPsbtData = await this.provider.signPsbt(psbt.toHex(), {
      autoFinalized: true,
      toSignInputs: inputsToSign,
    })

    const signedPsbt = bitcoinjs.Psbt.fromHex(signedPsbtData)
    const tx = signedPsbt.extractTransaction()
    const rawTxHex = tx.toHex()

    return await broadcastTx(networkId, rawTxHex)
  }
  async getAddress(_networkId?: string): Promise<string> {
    return this.address
  }

  async signMessage(message: string, network: string): Promise<string> {
    if (!this.provider) {
      throw new CustomError('Binance wallet is not installed')
    }

    if (isBtcChain(network || '')) {
      return await this.provider.signMessage(message)
    }

    throw new Error('Upnsupport network')
  }

  async getCompressedPublicKey(_network: string): Promise<string> {
    return this.publicKey
  }

  async signPsbt(psbt: string, network: string, options?: PsbtSignOptions): Promise<string> {
    if (!this.provider) {
      throw new CustomError('Binance wallet is not installed')
    }
    if (isBtcChain(network)) {
      const hexPsbt = bitcoinjs.Psbt.fromBase64(psbt).toHex()
      return await this.provider.signPsbt(hexPsbt, options)
    }

    throw new Error(`SignPsbt is not supported for network ${network}`)
  }

  isDetected(): boolean {
    return !!getProvider()
  }

  priority(): number {
    if (!isMobileDevice()) return -1
    return this.isInstalled() ? 0 : 1
  }
}
